// DataRequestFieldRow — Reusable read-only field row for data request responses

// Renders a single field row with: dot indicator, label, separator, response
// value (document/multi-doc/yesno/text/empty), type label, and status icon.
// Used by PageVoucher, PageContract, and any other view showing condition fields.

import { alpha, Box, Stack, Typography, useTheme } from '@mui/material';
import { CheckCircle as IconCheckCircle } from '@mui/icons-material';
import type { DataRequestField, DataRequestResponse } from '../../../@types';
import { COLORS } from '../../../data/colors';

/** Subset of V2DataRequestResponse fields needed for display */
type FieldResponse = Pick<
	DataRequestResponse,
	| 'value'
	| 'documentId'
	| 'documentName'
	| 'documentIds'
	| 'documentNames'
	| 'overridden'
	| 'overrideComment'
>;

// Field type → accent color
const FIELD_TYPE_COLORS: Record<string, string> = {
	document: '#1976d2',
	yesno: '#7b1fa2',
	text: '#546e7a',
	number: '#00897b',
	date: '#e65100',
	select: '#5c6bc0',
};

// Field type → display label
const FIELD_TYPE_LABELS: Record<string, string> = {
	document: 'Document',
	yesno: 'Yes / No',
	text: 'Text',
	number: 'Number',
	date: 'Date',
	select: 'Dropdown',
};

export interface DataRequestFieldRowProps {
	field: DataRequestField;
	response?: FieldResponse | null;
	/** Called when user clicks a document link */
	onDocClick?: (docId: string, docName: string) => void;
	/** Left padding (default 4) */
	pl?: number;
}

export const DataRequestFieldRow = ({
	field,
	response,
	onDocClick,
	pl = 4,
}: DataRequestFieldRowProps) => {
	const theme = useTheme();

	const hasResponse =
		!!response &&
		(response.value !== null || !!response.documentId || (response.documentIds?.length ?? 0) > 0);
	const isDocument = field.fieldType === 'document';
	const isYesNo = field.fieldType === 'yesno';
	const isRequired = !!field.required;
	const accentColor = FIELD_TYPE_COLORS[field.fieldType] || '#546e7a';

	// Multi-doc support: prefer arrays over single
	const docIds = response?.documentIds?.length
		? response.documentIds
		: response?.documentId
			? [response.documentId]
			: [];
	const docNames = response?.documentNames?.length
		? response.documentNames
		: response?.documentName
			? [response.documentName]
			: [];
	const hasMultipleDocs = docIds.length > 1;
	const hasDoc = isDocument && docNames.length > 0;

	// Canonical stored value for yesno fields is the string 'yes' / 'no'
	// (lowercase) — written by FieldRendererYesNo and the prepare-payment
	// dialog. Legacy 'true'/'1' is tolerated for any older rows.
	const yesNoNorm = String(response?.value ?? '').toLowerCase();
	const isYes = yesNoNorm === 'yes' || yesNoNorm === 'true' || yesNoNorm === '1';
	const isClickable = hasDoc && !!onDocClick;

	return (
		<Stack
			direction="row"
			alignItems="flex-start"
			spacing={1}
			onClick={
				isClickable && !hasMultipleDocs && onDocClick
					? () => onDocClick(docIds[0], docNames[0] || '')
					: undefined
			}
			sx={{
				py: 0.5,
				px: 2,
				pl,
				...(isClickable &&
					!hasMultipleDocs && {
						cursor: 'pointer',
						'&:hover': {
							bgcolor: alpha(theme.palette.primary.main, 0.04),
						},
					}),
			}}>
			{/* Dot indicator — wrapper matches text line-height so dot centers with first line */}
			<Box
				sx={{
					width: 8,
					height: '1.75rem',
					display: 'flex',
					alignItems: 'center',
					flexShrink: 0,
				}}>
				<Box
					sx={{
						width: 8,
						height: 8,
						borderRadius: '50%',
						bgcolor: hasResponse ? COLORS.success.main : alpha(accentColor, 0.4),
					}}
				/>
			</Box>
			{/* Field label */}
			<Typography
				sx={{
					fontSize: '1.25rem',
					lineHeight: '1.75rem',
					fontWeight: 500,
					color: hasResponse ? '#1F2937' : '#9CA3AF',
					flex: 1,
					minWidth: 0,
				}}>
				{field.label || FIELD_TYPE_LABELS[field.fieldType]}
			</Typography>
			{/* Right-side group: separator, response, type label, status icon */}
			<Stack
				direction="row"
				alignItems="center"
				spacing={1}
				sx={{ height: '1.75rem', flexShrink: 0 }}>
				{/* Separator */}
				<Typography sx={{ color: '#D1D5DB', fontSize: '1.25rem' }}>—</Typography>
				{/* Response value */}
				{hasResponse ? (
					isDocument && docNames.length > 0 ? (
						hasMultipleDocs ? (
							<Stack spacing={0.25}>
								{docIds.map((docId, idx) => (
									<Typography
										key={docId}
										onClick={
											onDocClick
												? (e) => {
														e.stopPropagation();
														onDocClick(docId, docNames[idx] || '');
													}
												: undefined
										}
										sx={{
											fontSize: '1.25rem',
											color: 'primary.main',
											fontWeight: 500,
											...(onDocClick && {
												cursor: 'pointer',
												'&:hover': { textDecoration: 'underline' },
											}),
										}}
										noWrap>
										{docNames[idx] || 'Document'}
									</Typography>
								))}
							</Stack>
						) : (
							<Typography
								sx={{
									fontSize: '1.25rem',
									color: 'primary.main',
									fontWeight: 500,
									maxWidth: 260,
									...(onDocClick && {
										'&:hover': { textDecoration: 'underline' },
									}),
								}}
								noWrap>
								{docNames[0]}
							</Typography>
						)
					) : isYesNo ? (
						<Typography
							sx={{
								fontSize: '1.25rem',
								fontWeight: 500,
								color: isYes ? COLORS.success.main : theme.palette.error.main,
							}}>
							{isYes ? 'Yes' : 'No'}
						</Typography>
					) : (
						<Typography
							sx={{
								fontSize: '1.25rem',
								fontWeight: 500,
								color: '#374151',
								maxWidth: 200,
							}}
							noWrap>
							{response?.overridden
								? `Override: ${response?.overrideComment || '—'}`
								: response?.value || '—'}
						</Typography>
					)
				) : (
					<Typography
						sx={{
							fontSize: '1.25rem',
							color: '#D1D5DB',
							fontStyle: 'italic',
						}}>
						{isRequired ? 'Required' : 'Optional'}
					</Typography>
				)}
				{/* Type label */}
				<Typography
					sx={{
						fontSize: '1.25rem',
						color: accentColor,
						fontWeight: 600,
						opacity: 0.7,
					}}>
					{FIELD_TYPE_LABELS[field.fieldType] || field.fieldType}
				</Typography>
				{/* Status indicator */}
				{hasResponse ? (
					<IconCheckCircle
						sx={{
							fontSize: 16,
							color: COLORS.success.main,
						}}
					/>
				) : isRequired ? (
					<Box
						sx={{
							width: 16,
							height: 16,
							borderRadius: '50%',
							border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
						}}
					/>
				) : (
					<Box sx={{ width: 16 }} />
				)}
			</Stack>
		</Stack>
	);
};
